import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { SearchModalHeader } from './SearchModalHeader';
import { SearchModalSidebar } from './SearchModalSidebar';
import { SearchModalContent } from './SearchModalContent';

import { useSearchType } from '../hooks';
import { Dialog } from '@headlessui/react';

import { useDebounce } from '@distribute/frontend/utils';

import { IconMap } from '../../../shared/sprite';
import { useSubscriptionLimits } from '../../../features/subscription/hooks';

type Props = {
  onClose: () => void;
};

export const SearchModal: FC<Props> = ({ onClose }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const { searchType, setSearchType, canSearchInFolder } = useSearchType();

  const { snippets: allowSnippets } = useSubscriptionLimits();

  const handleClear = useCallback(() => setSearch(''), []);

  const items = useMemo(() => {
    const _items = [
      {
        id: 'folder' as const,
        label: 'Current folder',
        glyph: IconMap.AddFolder,
        visible: canSearchInFolder,
      },
      {
        id: 'all-pages' as const,
        label: 'All pages',
        glyph: IconMap.Files,
        visible: true,
      },
      {
        id: 'videoRecord' as const,
        label: 'Video Recordings',
        glyph: IconMap.Film02,
        visible: true,
      },
      {
        id: 'callRecord' as const,
        label: 'Call Recordings',
        glyph: IconMap.HeadPhones02,
        visible: true,
      },
      {
        id: 'template' as const,
        label: 'Templates',
        glyph: IconMap.LayersThree01,
        visible: true,
      },
      {
        id: 'snippet' as const,
        label: 'Snippets',
        glyph: IconMap.Snippets,
        visible: allowSnippets,
      },
    ];

    return _items.filter((item) => item.visible);
  }, [canSearchInFolder, allowSnippets]);

  return (
    <Dialog className="relative z-100 w-full" open onClose={onClose}>
      <div className="fixed inset-0 flex items-center justify-center p-4 bg-bg-200">
        <Dialog.Panel className="w-full max-w-180 h-100 shadow-xl rounded-xl bg-base-white relative flex flex-col items-center overflow-hidden">
          <SearchModalHeader
            inputRef={inputRef}
            value={search}
            onChange={(value) => setSearch(value)}
          />
          <div className="flex flex-nowrap gap-x-5 justify-between w-full h-86">
            <SearchModalSidebar
              inputRef={inputRef}
              searchType={searchType}
              setSearchType={setSearchType}
              items={items}
            />
            <SearchModalContent
              searchType={searchType}
              search={debouncedSearch}
              onClear={handleClear}
              onClose={onClose}
            />
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
